import React, { FC, useEffect, useState } from 'react';

import { Modal, Text } from '../../../common/components';
import { Experiment, RolloutTarget } from '../../../lib/App';
import {
  ConstraintFacetsOptions,
  FacetType,
  VariableSearchColumn,
} from './VariablesSearch';
import VariablesSearchExperiment, {
  filterExperimentsMatchingFacet,
} from './VariablesSearchExperiment';
import { TOTAL_RETURNED_RESULT } from './VariablesSearchRecords';
import VariablesSearchRollout, {
  filterRolloutTargetMatchingFacet,
} from './VariablesSearchRollout';

import './VariablesSearch.css';

import { Card } from '@duxton-web/ui';
import { Divider, Select, Skeleton } from 'antd';

import CityTreeSelect from '../../../common/selects/CityTreeSelect';
import CountrySelect from '../../../common/selects/CountrySelect';
import VehicleTreeSelect from '../../../common/selects/VehicleTreeSelect';
import { renderVariableColumn } from './VariablesSearchVariableColumn';

interface OwnProps {
  variable: any;
  colIndex: number;
  columns: VariableSearchColumn[];
  total: number;
}

const VariablesSearchMore: FC<OwnProps> = ({
  variable,
  colIndex,
  columns,
  total,
}) => {
  const [open, updateOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [experiments, setExperiments] = useState<Map<number, Experiment>>(null);
  const [rolloutTargets, setRolloutTargets] =
    useState<Map<number, RolloutTarget>>(null);
  const [totalReturnedValues, setTotalReturnedValues] = useState<number>(0);

  useEffect(() => {
    if (open) {
      if (!experiments && !rolloutTargets) {
        const rollout = variable?.rollouts;
        const experiments = variable?.experiments;
        const matchingExperiments: Map<number, Experiment> =
          filterExperimentsMatchingFacet(experiments, columns, colIndex, true);

        const experimentsCount =
          Array.from(matchingExperiments.keys())?.length || 0;

        const matchingRolloutTarget: Map<number, RolloutTarget> =
          filterRolloutTargetMatchingFacet(
            rollout?.rollouts,
            columns,
            colIndex,
            -1,
          );

        const rolloutTargetsCount =
          Array.from(matchingRolloutTarget.keys())?.length || 0;

        setExperiments(matchingExperiments);
        setRolloutTargets(matchingRolloutTarget);
        setTotalReturnedValues(experimentsCount + rolloutTargetsCount);
      } else {
        setExperiments(null);
        setRolloutTargets(null);
        setTotalReturnedValues(0);
      }
    }

    if (loading) setLoading(false);
  }, [open]);

  const renderContent = () => {
    if (loading)
      return (
        <Skeleton
          active
          paragraph={{
            rows: 14,
          }}
        />
      );

    return (
      <div
        className="flex-col half-gap"
        style={{
          overflow: 'hidden auto',
        }}
      >
        <VariablesSearchExperiment
          variable={variable}
          matchingExperiments={experiments}
          colIndex={colIndex}
          columns={columns}
          renderAll={true}
        />
        <VariablesSearchRollout
          variable={variable}
          matchingRolloutTarget={rolloutTargets}
          colIndex={colIndex}
          columns={columns}
          renderAll={true}
        />
      </div>
    );
  };

  const renderMoreButton = () => {
    if (total > TOTAL_RETURNED_RESULT - 1) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '28px',
            backgroundColor: '#EEF9F9',
            borderRadius: '48px',
            cursor: 'pointer',
          }}
          onClick={() => {
            if (!experiments && !rolloutTargets) setLoading(true);

            updateOpen(true);
          }}
        >
          <Text
            style={{
              color: '#277C74',
            }}
            strong
          >
            View All Returned Values
          </Text>
        </div>
      );
    }

    return null;
  };

  const renderConstraint = () => {
    return (
      <div
        className="flex-row-center-aligned half-gap w100"
        style={{
          padding: '16px',
        }}
      >
        <div className="field-container flex-one-one">
          <Text
            style={{
              fontSize: '12px',
              lineHeight: '16px',
            }}
            ellipsis
          >
            Facet
          </Text>
          <Select
            disabled
            value={columns[colIndex]?.constraint?.facet}
            className="grab-x__variables-search-select"
            options={ConstraintFacetsOptions}
          />
        </div>
        <div className="field-container flex-one-one">
          <Text
            style={{
              fontSize: '12px',
              lineHeight: '16px',
            }}
          >
            Select Value
          </Text>
          {columns[colIndex].constraint.facet === FacetType.COUNTRY && (
            <CountrySelect
              disabled
              value={columns[colIndex]?.constraint?.value}
            />
          )}
          {columns[colIndex].constraint.facet === FacetType.CITY && (
            <CityTreeSelect
              disabled
              value={columns[colIndex]?.constraint?.value}
              onChange={() => {}}
            />
          )}
          {columns[colIndex]?.constraint.facet ===
            FacetType.VECHILE_TYPE_ID && (
            <VehicleTreeSelect
              disabled
              value={columns[colIndex]?.constraint?.value}
              onChange={() => {}}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {renderMoreButton()}
      <Modal
        className="variables-search-more"
        open={open}
        title={`All Returned Values (${totalReturnedValues})`}
        onCancel={() => {
          updateOpen(false);
        }}
        footer={null}
        bodyStyle={{
          padding: '24px',
          height: '800px',
          overflow: 'hidden',
        }}
        width={720}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            height: '100%',
          }}
        >
          <Card
            styles={{
              body: {
                padding: 0,
              },
            }}
          >
            <div
              style={{
                padding: '16px',
                overflow: 'hidden',
              }}
            >
              {renderVariableColumn(variable)}
            </div>
            <Divider
              style={{
                margin: 0,
              }}
            />
            {renderConstraint()}
          </Card>
          {renderContent()}
        </div>
      </Modal>
    </>
  );
};

export default VariablesSearchMore;
